import create from "zustand";
import axios from "axios";
import { apiUrl } from "./apiUrl";
import jwtDecode, { JwtPayload } from "jwt-decode";
import { BehaviorSubject, Subject, delay, throttleTime } from "rxjs";

const RefreshAccessTokenBeforeExpirationMinutes = 60;

const InactivityLogoffMinutes = 60;
const InactivityWarningInAdvanceMinutes = 15;
const InactivityWarningUpdateIntervalMinutes = 1;

export const accountDataKey = "account";
export const lastActivityTimeKey = "lastActivityTime";

export interface AuthResponse {
    accessToken: string | null;
    userMfaSessionId: string | null;
    isUserMfaSessionCodeRequired: boolean;
    errorMessage: string;
    accountInfo: AccountInfoDto | null;
}

export function isResponseUnauthorized(err: any) {
    return err?.response?.status === 401;
}

export enum UserTypeId {
    Admin = 1,
    User = 2,
    View = 3,
    Supervisor = 4,
    CollectionAgency = 5,
    TmSupervisor = 6,
    TmAgent = 7,
    TmManager = 8,
}

export interface AccountStore {
    data: AccountStoreData | null;
    data$: BehaviorSubject<AccountStoreData | null>;
    isTokenBeingRefreshed: boolean;
    inactivityTimer: number | null;
    inactivityRemainingMinutes: number | null;
    setData: (accountStoreData: AccountStoreData | null, isFromLocalStorage?: true) => void;
    getAndRefreshAccessToken: () => string | null;
    refreshAccessToken: (force?: boolean) => Promise<void>;
    resetInactivityTimer: (setToLocalStorage?: boolean) => void;
    updateLocalStorage(): void;
}

export interface AccountData {
    accessToken: string;
    accountInfo: AccountInfoDto;
}

export interface AccountStoreData {
    accountData: AccountData;
    accountDataRaw: string;
    accessTokenDecoded: AccessTokenDecoded;
}

export interface AccessTokenDecoded {
    userTypeId: UserTypeId;
    userId: number;
}

export interface AccountInfoDto {
    callCenterId: number | null;
    language: string;
    hideCherrypickData: boolean;
}

export const isTokenExpired = (token: string, offsetSeconds = 0): boolean => {
    try {
        const { exp } = jwtDecode<JwtPayload>(token);
        const expirationDatetimeInMs = (exp || Number.MAX_VALUE) * 1000;
        return Date.now() >= expirationDatetimeInMs - offsetSeconds * 1000;
    } catch {
        return true;
    }
};

export function decodeAccessToken(accessToken: string): AccessTokenDecoded {
    const decoded = jwtDecode<{ role: UserTypeId; sub: string; }>(accessToken);
    const userTypeId = decoded.role;
    const userId = Number(decoded.sub);
    return { userTypeId, userId };
}

function getAccountStoreDataFromDataRaw(accountDataRaw: string | null) {
    if (accountDataRaw == null) {
        return null;
    }

    const accountData = JSON.parse(accountDataRaw) as AccountData;
    const accountStoreData: AccountStoreData = {
        accountData,
        accountDataRaw,
        accessTokenDecoded: decodeAccessToken(accountData.accessToken),
    };
    return accountStoreData;
}

export function getAccountStoreDataFromData(accountData: AccountData | null) {
    if (accountData == null) {
        return null;
    }

    const accountDataRaw = JSON.stringify(accountData);
    const accountStoreData: AccountStoreData = {
        accountData,
        accountDataRaw,
        accessTokenDecoded: decodeAccessToken(accountData.accessToken),
    };
    return accountStoreData;
}

export const useAccountStore = create<AccountStore>((set, get) => {
    function getInitialData() {
        const accountStoreData = getAccountStoreDataFromDataRaw(localStorage.getItem(accountDataKey));
        return {
            data: accountStoreData,
            data$: new BehaviorSubject<AccountStoreData | null>(accountStoreData),
        };
    }
    return {
        ...getInitialData(),
        isTokenBeingRefreshed: false,
        inactivityTimer: null,
        inactivityRemainingMinutes: null,
        setData: (accountStoreData, isFromLocalStorage?: true) => {
            set({ data: accountStoreData });
            get().data$.next(accountStoreData);
            if (!isFromLocalStorage) {
                get().updateLocalStorage();
            }
        },
        getAndRefreshAccessToken() {
            const token = get().data?.accountData.accessToken;
            if (token == null) {
                return null;
            }
            get().refreshAccessToken();
            return token;
        },
        resetInactivityTimer(setToLocalStorage = true) {
            if (get().inactivityTimer != null) {
                window.clearTimeout(get().inactivityTimer!);
            }
            const lastActivityTime = Date.now();
            const updateLogoutTime = () => {
                const remainingMinutes = InactivityLogoffMinutes - (Date.now() - lastActivityTime) / 1000 / 60;
                if (remainingMinutes <= 0) {
                    get().setData(null);
                    return;
                }
                set({
                    inactivityRemainingMinutes: remainingMinutes,
                    inactivityTimer: window.setTimeout(updateLogoutTime, InactivityWarningUpdateIntervalMinutes * 60 * 1000),
                });
            };
            set({
                inactivityRemainingMinutes: null,
                inactivityTimer: window.setTimeout(updateLogoutTime, (InactivityLogoffMinutes - InactivityWarningInAdvanceMinutes) * 60 * 1000),
            });
            if (setToLocalStorage) {
                localStorage.setItem(lastActivityTimeKey, lastActivityTime.toString());
            }
        },
        async refreshAccessToken(force = false) {
            const token = get().data?.accountData.accessToken;
            if (token == null || get().isTokenBeingRefreshed) {
                return;
            }
            if (force || isTokenExpired(token, RefreshAccessTokenBeforeExpirationMinutes * 60)) {
                set({ isTokenBeingRefreshed: true });
                try {
                    const { data } = await axios.post<AuthResponse | null>(apiUrl() + "account/refreshToken", null, { headers: { Authorization: `Bearer ${token}` } });
                    const accountStoreData = getAccountStoreDataFromData(data == null ? null : { accessToken: data.accessToken!, accountInfo: data.accountInfo! });
                    get().setData(accountStoreData);
                } catch (err) {
                    if (isResponseUnauthorized(err)) {
                        get().setData(null);
                    }
                } finally {
                    set({ isTokenBeingRefreshed: false });
                }
            }
        },
        updateLocalStorage() {
            const accountDataRaw = get().data?.accountDataRaw ?? null;
            if (localStorage.getItem(accountDataKey) !== accountDataRaw) {
                if (accountDataRaw == null) {
                    localStorage.removeItem(accountDataKey);
                } else {
                    localStorage.setItem(accountDataKey, accountDataRaw);
                }
            }
        },
    };
});

window.addEventListener("storage", e => {
    if (e.key === accountDataKey) {
        const accountDataRaw = useAccountStore.getState().data?.accountDataRaw ?? null;
        if (e.newValue !== accountDataRaw) {
            useAccountStore.getState().setData(getAccountStoreDataFromDataRaw(accountDataRaw), true);
        }
    }

    if (e.key === lastActivityTimeKey) {
        useAccountStore.getState().resetInactivityTimer(false);
    }
});

const inactivityRateLimitSubject = new Subject<void>();
inactivityRateLimitSubject.pipe(throttleTime(1000), delay(100)).subscribe(() => {
    useAccountStore.getState().resetInactivityTimer();
});

window.addEventListener("click", () => {
    inactivityRateLimitSubject.next();
});

window.addEventListener("keydown", () => {
    inactivityRateLimitSubject.next();
});

useAccountStore.getState().getAndRefreshAccessToken();
useAccountStore.getState().resetInactivityTimer();
